import {
  apiAbortController,
  Currency,
  Response,
  Signature,
  TransactionWarning,
} from '@ui/src';
import api from '../../../api/api';
import { WarningsInfo } from '../../transactions/type';
import {
  Order,
  PaymentOrder,
  PaymentOrderFormValues,
  PaymentOrderStatuses,
  WarningGroup,
} from '../types';

export const cashCollectionListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPaymentsOrders: build.query<Response<PaymentOrder>, string>({
      query: (query) => ({
        url: `/v1/payments-orders${query}${
          query ? '&' : '?'
        }type=CASH_COLLECTION_SINGLE_OUTPUT`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrder: build.query<{ data: PaymentOrder }, string>({
      query: (id) => ({
        url: `/v1/payments-orders/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrderWarnings: build.query<Response<WarningGroup>, string>({
      query: (id) => ({
        url: `/v1/payments-orders/${id}/warnings-groups?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrderWarningsWithPagination: build.query<
      Response<TransactionWarning>,
      { id: string; warningGroupId: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}/warnings-groups/${data.warningGroupId}/warnings?cursor=${data.cursor}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrderWarning: build.query<
      { data: TransactionWarning },
      {
        poId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.poId}/warnings-groups/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrderExtended: build.query<
      { data: { beneficiaries: Order[] } },
      string
    >({
      query: (id) => ({
        url: `/v1/payments-orders/${id}/extended`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentOrderDocumentFile: build.query<
      { data: any },
      { id: string; uploadId: string }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}/file/${data.uploadId}`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrderSignatures: build.query<Response<Signature>, string>({
      query: (id) => ({
        url: `/v1/payments-orders/${id}/signatures`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrderStatuses: build.query<
      { data: PaymentOrderStatuses },
      { id: string; statusPrev: string }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}/statuses?statusPrev=${data.statusPrev}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePaymentOrder: build.mutation<
      { data: PaymentOrder },
      { body: PaymentOrderFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePaymentOrderStatus: build.mutation<
      { data: PaymentOrder },
      {
        body: {
          status: string;
          currencyId: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPaymentOrder: build.mutation<
      { data: PaymentOrder },
      PaymentOrderFormValues
    >({
      query: (body) => ({
        url: '/v1/payments-orders',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCashCollectionSignature: build.mutation<
      { data: Signature },
      {
        id: string;
        body: {
          mfaToken: string;
          signedAt: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}/sign`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchApplyWarningAction: build.mutation<
      { data: any },
      {
        poId: string;
        body: {
          warningGroupId: string;
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.poId}/warnings-groups/warnings/batch-action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        poId: string;
        id: string;
        warningId: string;
        body: {
          action: string;
          comment: string;
          employeeId: string;
          matchStatus?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.poId}/warnings-groups/${data.id}/warnings/${data.warningId}/action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportCashCollectionTransaction: build.mutation<
      any,
      Record<string, string | number | string[]>
    >({
      query: (body) => ({
        url: '/v1/payments-orders/csv',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportOrdersTransaction: build.mutation<
      any,
      Record<string, string | number | string[]>
    >({
      query: (body) => ({
        url: '/v1/payments-orders-beneficiaries/csv',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetPaymentOrderTransactionWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        poId: string;
        id: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.poId}/warnings-groups/${data.id}/warnings/${data.warningId}/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrdersBeneficiaries: build.query<Response<Order>, string>({
      query: (query) => ({
        url: `/v1/payments-orders-beneficiaries${query}${
          query ? '&' : '?'
        }types=CASH_COLLECTION_SINGLE_OUTPUT`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentsOrdersBeneficiary: build.query<{ data: Order }, string>({
      query: (id) => ({
        url: `/v1/payments-orders-beneficiaries/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentOrderWarningsInfo: build.query<
      { data: WarningsInfo },
      { id: string; query: string }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.id}/warnings/info?${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWarningGroupBeneficiaries: build.query<
      Response<{
        id: string;
        senderName?: string;
        localAmount: number;
        localCurrency: Currency;
        destAmount: number;
        destCurrency: Currency;
        createdAt: string;
        paymentOrderId?: string;
        paymentOrder?: {
          recepientAccount?: {
            organization: {
              id: string;
              companyName?: string;
              type?: string;
              firstName?: string;
              lastName?: string;
            };
          };
        };
      }>,
      { poId: string; id: string; warningId: string; cursor: string }
    >({
      query: (data) => ({
        url: `/v1/payments-orders/${data.poId}/warnings-groups/${data.id}/warnings/${data.warningId}/beneficiaries?take=20&cursor=${data.cursor}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetPaymentsOrdersQuery,
  useLazyGetPaymentsOrderQuery,
  useLazyGetPaymentsOrderStatusesQuery,
  useUpdatePaymentOrderMutation,
  useUpdatePaymentOrderStatusMutation,
  useLazyGetPaymentsOrderSignaturesQuery,
  useLazyGetPaymentOrderDocumentFileQuery,
  useLazyGetPaymentsOrderExtendedQuery,
  useCreatePaymentOrderMutation,
  useCreateCashCollectionSignatureMutation,
  useLazyGetPaymentsOrderWarningsQuery,
  useBatchApplyWarningActionMutation,
  useApplyWarningActionMutation,
  useExportCashCollectionTransactionMutation,
  useResetPaymentOrderTransactionWarningActionMutation,
  useLazyGetPaymentsOrderWarningsWithPaginationQuery,
  useLazyGetPaymentsOrdersBeneficiariesQuery,
  useLazyGetPaymentsOrdersBeneficiaryQuery,
  useExportOrdersTransactionMutation,
  useLazyGetPaymentsOrderWarningQuery,
  useLazyGetWarningGroupBeneficiariesQuery,
  useLazyGetPaymentOrderWarningsInfoQuery,
} = cashCollectionListApi;

export const {
  endpoints: {
    getPaymentsOrders,
    getPaymentsOrder,
    getPaymentsOrderStatuses,
    updatePaymentOrder,
    updatePaymentOrderStatus,
    getPaymentsOrderSignatures,
    getPaymentOrderDocumentFile,
    getPaymentsOrderExtended,
    createPaymentOrder,
    createCashCollectionSignature,
    getPaymentsOrderWarnings,
    batchApplyWarningAction,
    applyWarningAction,
    exportCashCollectionTransaction,
    resetPaymentOrderTransactionWarningAction,
    getPaymentsOrderWarningsWithPagination,
    getPaymentsOrdersBeneficiaries,
    getPaymentsOrdersBeneficiary,
    exportOrdersTransaction,
    getPaymentsOrderWarning,
    getWarningGroupBeneficiaries,
    getPaymentOrderWarningsInfo,
  },
} = cashCollectionListApi;
